import { Shield, FloppyDisk, ArrowLeft, Warning } from "@phosphor-icons/react";
import {
  Button,
  Card,
  Checkbox,
  Label,
  Spinner,
  TextInput,
  Textarea,
} from "flowbite-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AnimatedAccordion from "../../../../components/AnimatedAccordion";
import AnimatedModal from "../../../../components/AnimatedModal";
import RoleFormSkeleton from "../../../../components/SkeletonLoaders/RoleFormSkeleton";
import { useCategorizedPermissions } from "../../../../hooks/get/useCategorizedPermissions";
import { useRole } from "../../../../hooks/get/useRole";
import { useCreateRole } from "../../../../hooks/post/useCreateRole";
import { useUpdateRole } from "../../../../hooks/put/useUpdateRole";
import AppLogo from "../../../AppCountryReports/components/AppLogo";
import {
  ApplicationPermission,
  ModulePermission,
  Permission,
  IntegrationPairPermission,
} from "../interface";

// Custom hook to generate a stable array of refs
function useArrayOfRefs<T>(length: number) {
  const refs = useRef<React.RefObject<T>[]>([]);
  if (refs.current.length !== length) {
    refs.current = Array(length)
      .fill(null)
      .map((_, i) => refs.current[i] || React.createRef<T>());
  }
  return refs.current;
}

interface RoleFormValues {
  name: string;
  description: string;
  selectedPermissions: number[];
}

const RoleForm = () => {
  const { t } = useTranslation();
  const { id: roleId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [expandedModules, setExpandedModules] = useState<{
    [key: string]: boolean;
  }>({
    "Role Management": true,
    "Team Management": true,
  });
  const [expandedIntegrationPairs, setExpandedIntegrationPairs] = useState<{
    [key: string]: boolean;
  }>({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [formData, setFormData] = useState<RoleFormValues | null>(null);

  const isEditMode = useMemo(() => roleId, [roleId]);

  // Fetch role details if editing
  const { data: roleData, isLoading: isLoadingRole } = useRole(roleId || "");

  // Fetch categorized permissions
  const { data: availablePermissions, isLoading: isLoadingPermissions } =
    useCategorizedPermissions();

  // React Hook Form
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    trigger,
    reset,
  } = useForm<RoleFormValues>({
    defaultValues: {
      name: "",
      description: "",
      selectedPermissions: [],
    },
    mode: "onSubmit",
    criteriaMode: "all",
    resolver: undefined,
    shouldFocusError: true,
    reValidateMode: "onChange",
  });

  // Reset form on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Create and Update mutations
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();

  // Set initial permissions when data is loaded
  useEffect(() => {
    if (availablePermissions) {
      if (roleData) {
        // Get module permissions
        const modulePerms =
          roleData.permissions?.modules?.flatMap(
            (module: ModulePermission) =>
              module.permissions?.map((perm: Permission) => Number(perm.id)) ||
              []
          ) || [];

        // Get integration pair permissions by matching with available integration pairs
        const integrationPairPerms =
          roleData.permissions?.integration_pair
            ?.map((pair: IntegrationPairPermission) => {
              let pairIndex = availablePermissions.integration_pairs?.findIndex(
                (p) => p.id.toString() === pair.id.toString()
              );
              if (pairIndex === -1 || pairIndex === undefined) {
                pairIndex = availablePermissions.integration_pairs?.findIndex(
                  (p) => p.codename === pair.codename
                );
              }
              return pairIndex !== undefined && pairIndex !== -1
                ? 10000 + pairIndex
                : null;
            })
            .filter((id): id is number => id !== null) || [];

        // Get application permissions by matching with available applications
        const appPerms =
          roleData.permissions?.application
            ?.map((app: ApplicationPermission) => {
              let appIndex = availablePermissions.application?.findIndex(
                (a) => a?.id?.toString() === app?.id?.toString()
              );
              if (appIndex === -1 || appIndex === undefined) {
                appIndex = availablePermissions.application?.findIndex(
                  (a) => a?.codename === app?.codename
                );
              }
              return appIndex !== undefined && appIndex !== -1
                ? 20000 + appIndex
                : null;
            })
            .filter((id): id is number => id !== null) || [];

        const selectedPerms = [...modulePerms, ...integrationPairPerms, ...appPerms];

        setValue("selectedPermissions", selectedPerms, {
          shouldValidate: true,
        });
      } else {
        // For new roles, select all module permissions by default
        const excludedCodenames = [
          "role_management_update",
          "role_management_delete",
          "team_management_update",
          "team_management_delete",
        ];
        const allModuleIds =
          availablePermissions.modules?.flatMap(
            (module) =>
              module.permissions
                ?.filter(
                  (perm) =>
                    typeof perm.codename === "string" &&
                    !excludedCodenames.includes(perm.codename)
                )
                .map((perm) => perm.id) || []
          ) || [];
        // Do not select any accounts by default for new roles
        const allPermissions = [...allModuleIds];
        setValue("selectedPermissions", allPermissions, {
          shouldValidate: true,
        });
      }
    }
  }, [setValue, roleData, availablePermissions, isEditMode]);

  // Set form values when role data is loaded
  useEffect(() => {
    if (roleData) {
      setValue("name", roleData.name);
      setValue("description", roleData.description || "");
    }
  }, [roleData, setValue]);

  // Modules
  const modules = availablePermissions?.modules || [];
  // Integration Pairs
  const integrationPairs = availablePermissions?.integration_pairs || [];
  const integrationPairIds = integrationPairs.map((_, idx) => 10000 + idx);
  // Applications
  const applications = availablePermissions?.application || [];
  // Group applications by integration pair
  const applicationsByIntegrationPair = integrationPairs.map((pair) => {
    const pairApps = applications.filter(
      (app) => app.tracking_id === pair.tracking_id.toString()
    );
    const pairAppIds = pairApps.map(
      (_, idx) => 20000 + applications.indexOf(pairApps[idx])
    );
    return {
      integrationPair: pair,
      applications: pairApps,
      appIds: pairAppIds,
    };
  });

  // Refs for modules and integration pairs
  const moduleRefs = useArrayOfRefs<HTMLInputElement>(modules.length);
  const integrationPairRefs = useArrayOfRefs<HTMLInputElement>(
    applicationsByIntegrationPair.length
  );

  // Watch selected permissions
  const selectedPermissions = watch("selectedPermissions");

  // Select/deselect all modules
  const isAllModulesSelected =
    modules.length > 0 &&
    modules.every((module) =>
      module.permissions?.every((perm) => selectedPermissions.includes(perm.id))
    );

  const handleRootModulesToggle = (checked: boolean) => {
    let updatedPermissions = [...selectedPermissions];

    // Filter out all existing module permissions
    updatedPermissions = updatedPermissions.filter(
      (id) =>
        !modules.some((module) =>
          module.permissions?.some((perm) => perm.id === id)
        )
    );

    // Add all module permissions if checked
    if (checked) {
      const allModulePermissions = modules.flatMap(
        (module) => module.permissions?.map((perm) => perm.id) || []
      );
      updatedPermissions = [...updatedPermissions, ...allModulePermissions];
    }

    setValue("selectedPermissions", updatedPermissions, {
      shouldValidate: true,
    });
    trigger("selectedPermissions");
  };

  // Select/deselect all integration pairs and apps
  const isAllIntegrationPairsAndAppsSelected =
    integrationPairIds.length > 0 &&
    integrationPairIds.every((id) => selectedPermissions.includes(id));
  const handleRootIntegrationPairsToggle = (checked: boolean) => {
    // Get all integration pair IDs
    const allIntegrationPairIds = integrationPairs.map((_, idx) => 10000 + idx);
    // Get all application IDs
    const allAppIds = applications.map((_, idx) => 20000 + idx);

    let newSelected = selectedPermissions.filter(
      (id) => !allIntegrationPairIds.includes(id) && !allAppIds.includes(id)
    );
    if (checked) newSelected = [...newSelected, ...allIntegrationPairIds, ...allAppIds];
    setValue("selectedPermissions", newSelected, { shouldValidate: true });
    trigger("selectedPermissions");
  };

  // Toggle all permissions in a group
  const handleGroupSelectAll = (ids: number[], checked: boolean) => {
    let groupPermissions = [...selectedPermissions];

    // Remove the specific group's permissions
    groupPermissions = groupPermissions.filter((id) => !ids.includes(id));

    // Add all permissions if checked
    if (checked) {
      groupPermissions = [...groupPermissions, ...ids];
    }

    setValue("selectedPermissions", groupPermissions, { shouldValidate: true });
    trigger("selectedPermissions");
  };

  // Toggle a single permission
  const handlePermissionToggle = (permId: number, checked: boolean) => {
    let newSelected = [...selectedPermissions];
    if (checked) {
      if (!newSelected.includes(permId)) {
        newSelected.push(permId);
      }
    } else {
      newSelected = newSelected.filter((id) => id !== permId);
    }
    setValue("selectedPermissions", newSelected, { shouldValidate: true });
    trigger("selectedPermissions");
  };

  // Handle integration pair selection with its applications
  const handleIntegrationPairToggle = (
    pairId: number,
    checked: boolean,
    appIds: number[]
  ) => {
    let newSelected = [...selectedPermissions];
    if (checked) {
      if (!newSelected.includes(pairId)) newSelected.push(pairId);
      // Only add app IDs if they exist (for integration pairs with applications)
      if (appIds.length > 0) {
        appIds.forEach((id) => {
          if (!newSelected.includes(id)) newSelected.push(id);
        });
      }
    } else {
      newSelected = newSelected.filter(
        (id) => id !== pairId && !appIds.includes(id)
      );
    }
    setValue("selectedPermissions", newSelected, { shouldValidate: true });
    trigger("selectedPermissions");
  };

  // Handle application selection with integration pair
  const handleApplicationToggle = (
    appId: number,
    pairId: number,
    checked: boolean,
    allAppIds: number[]
  ) => {
    let newSelected = [...selectedPermissions];
    if (checked) {
      if (!newSelected.includes(appId)) newSelected.push(appId);
      if (!newSelected.includes(pairId)) newSelected.push(pairId);
    } else {
      newSelected = newSelected.filter((id) => id !== appId);
      // If no other apps for this integration pair are selected, remove pair
      const anyOtherAppSelected = allAppIds.some(
        (id) => id !== appId && newSelected.includes(id)
      );
      if (!anyOtherAppSelected && allAppIds.length > 0) {
        newSelected = newSelected.filter((id) => id !== pairId);
      }
    }
    setValue("selectedPermissions", newSelected, { shouldValidate: true });
    trigger("selectedPermissions");
  };

  // Check if any integration pairs are selected
  const hasSelectedIntegrationPairs = useMemo(() => {
    if (!availablePermissions?.integration_pairs) return false;
    return availablePermissions.integration_pairs.some((_, idx) =>
      selectedPermissions.includes(10000 + idx)
    );
  }, [selectedPermissions, availablePermissions?.integration_pairs]);

  // Submit handler
  const onSubmit = async (data: RoleFormValues) => {
    if (!data.selectedPermissions.length) {
      trigger("selectedPermissions");
      return;
    }

    // Always show confirmation modal for both create and update
    setFormData(data);
    setShowConfirmationModal(true);
  };

  // Actual role submission logic
  const submitRole = async (data: RoleFormValues) => {
    // Build payload in required format
    const payload = {
      name: data.name,
      description: data.description,
      permission_data: {
        modules: modules
          .map((module) => ({
            module: module.module,
            permissions: (module.permissions || [])
              .filter((perm) => data.selectedPermissions.includes(perm.id))
              .map((perm) => ({
                id: perm.id,
                name: perm.name,
                action: perm.action,
                codename: perm.codename,
                description: perm.description,
              })),
          }))
          .filter((m) => m.permissions.length > 0),
        integration_pair: (availablePermissions?.integration_pairs || [])
          .filter((_, idx) => data.selectedPermissions.includes(10000 + idx))
          .map((pair) => ({
            name: pair.name,
            codename: pair.codename,
            tracking_id: pair.tracking_id,
          })),
        application: (availablePermissions?.application || [])
          .filter((_, idx) => data.selectedPermissions.includes(20000 + idx))
          .map((app) => ({
            id: app.id,
            codename: app.codename,
            name: app.name,
            platform: app.platform,
            logo: app.logo,
            store_id: app.store_id,
            slug: app.slug,
            user_id: app.user_id,
          })),
      },
    };

    try {
      if (roleId) {
        await updateRole.mutateAsync({ roleId, data: payload });
        toast.success(t("configurations.roles.updateSuccess"));
      } else {
        await createRole.mutateAsync(payload, {
          onSuccess: (data) => {
            navigate(`/role-management/${data.id}`);
            toast.success(t("configurations.roles.createSuccess"));
          },
        });
      }
    } catch {
      toast.error(
        roleId
          ? t("configurations.roles.updateError")
          : t("configurations.roles.createError")
      );
    }
  };

  // Handle confirmation modal confirm
  const handleConfirmCreate = async () => {
    if (formData) {
      setShowConfirmationModal(false);
      await submitRole(formData);
    }
  };

  // Show loader while fetching role data or permissions
  if (isLoadingPermissions || (isEditMode && isLoadingRole)) {
    return <RoleFormSkeleton />;
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 px-2">
        <div className="flex items-center gap-2">
          <Shield size={20} className="text-gray-700" weight="duotone" />
          <h1 className="text-md font-bold text-gray-700">
            {roleId
              ? t("configurations.roles.edit")
              : t("configurations.roles.create")}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button color="light" size="xs" onClick={() => navigate(-1)} className="flex-1 sm:flex-none">
            <div className="flex items-center gap-2">
              <ArrowLeft size={20} />
              {t("common.back")}
            </div>
          </Button>
          <Button
            size={"xs"}
            type="submit"
            color="indigo"
            className="flex-1 sm:flex-none"
            disabled={isSubmitting}
            isProcessing={isSubmitting}
            processingSpinner={<Spinner color="purple" size="sm" />}
            onClick={handleSubmit(onSubmit)}
          >
            <div className="flex items-center gap-2">
              <FloppyDisk size={20} />
              {roleId ? t("common.update") : t("common.create")}
            </div>
          </Button>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-2 ">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="name">{t("configurations.roles.name")}</Label>
            <TextInput
              id="name"
              {...register("name", { required: t("validation.required") })}
              disabled={isSubmitting}
              color={errors.name ? "failure" : undefined}
            />
            {errors.name && (
              <span className="text-xs text-red-500">
                {errors.name.message}
              </span>
            )}
          </div>
          <div>
            <Label htmlFor="description">
              {t("configurations.roles.description")}
            </Label>
            <Textarea
              rows={1}
              id="description"
              {...register("description")}
              disabled={isSubmitting}
            />
          </div>
        </div>
        {/* Show error message for permissions here */}
        {errors.selectedPermissions && (
          <div className="text-xs text-red-500 mb-2">
            {errors.selectedPermissions.message}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 ">
          {/* Left: Modules */}
          <Card
            theme={{
              root: {
                children: "flex h-full flex-col gap-1 p-4",
              },
            }}
          >
            <div className="flex justify-between items-center gap-2 mb-2">
              <h2 className="font-bold">{t("common.modules")}</h2>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="allModules"
                  className="cursor-pointer select-none"
                >
                  {t("common.selectAll")}
                </Label>
                <Checkbox
                  color={"default"}
                  id="allModules"
                  checked={isAllModulesSelected}
                  ref={moduleRefs[0]}
                  onChange={(e) => handleRootModulesToggle(e.target.checked)}
                  className="cursor-pointer select-none"
                />
              </div>
            </div>
            <div className="space-y-2 max-h-[calc(100vh-310px)] overflow-y-auto">
              {modules.map((module) => {
                const ids = module.permissions?.map((perm) => perm.id) || [];
                const allChecked =
                  ids.length > 0 &&
                  ids.every((id) => selectedPermissions.includes(id));
                const someChecked =
                  ids.some((id) => selectedPermissions.includes(id)) &&
                  !allChecked;
                return (
                  <AnimatedAccordion
                    checkBoxColor="default"
                    key={module.module}
                    title={module.module}
                    checked={allChecked}
                    indeterminate={someChecked}
                    onToggleAll={(checked) =>
                      handleGroupSelectAll(ids, checked)
                    }
                    expanded={!!expandedModules[module.module]}
                    onToggleExpand={() =>
                      setExpandedModules((prev) => ({
                        ...prev,
                        [module.module]: !prev[module.module],
                      }))
                    }
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 px-8">
                      {module.permissions?.map((perm) => (
                        <Label
                          key={perm.id}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <Checkbox
                            color="secondary"
                            checked={selectedPermissions.includes(perm.id)}
                            className="cursor-pointer select-none"
                            onChange={(e) =>
                              handlePermissionToggle(perm.id, e.target.checked)
                            }
                          />
                          <span>{perm.action}</span>
                        </Label>
                      ))}
                    </div>
                  </AnimatedAccordion>
                );
              })}
            </div>
          </Card>
          {/* Right: Integration Pairs & Applications */}
          <Card
            theme={{
              root: {
                children: "flex h-full flex-col gap-1 p-4",
              },
            }}
          >
            <div className="flex justify-between items-center gap-2 mb-2">
              <h2 className="font-bold">{t("configurations.integrations.title")}</h2>
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="allIntegrationPairs"
                  className="cursor-pointer select-none"
                >
                  {t("common.selectAll")}
                </Label>
                <Checkbox
                  color="default"
                  id="allIntegrationPairs"
                  checked={isAllIntegrationPairsAndAppsSelected}
                  ref={integrationPairRefs[0]}
                  onChange={(e) => handleRootIntegrationPairsToggle(e.target.checked)}
                  className="cursor-pointer select-none"
                />
              </div>
            </div>
            <div className="space-y-4 max-h-[calc(100vh-310px)] overflow-y-auto">
              {applicationsByIntegrationPair.map(
                ({ integrationPair, applications, appIds }) => {
                  const pairId = integrationPairIds[integrationPairs.indexOf(integrationPair)];
                  const appIdsLength = appIds.length;
                  const selectedAppIds = appIds.filter((id) =>
                    selectedPermissions.includes(id)
                  );
                  const pairSelected =
                    selectedPermissions.includes(pairId);
                  const allChecked =
                    (appIdsLength > 0 &&
                      appIds.every((id) => selectedPermissions.includes(id))) ||
                    (appIdsLength === 0 && pairSelected);
                  const someChecked =
                    appIdsLength > 0 &&
                    appIds.some((id) => selectedPermissions.includes(id)) &&
                    !allChecked;
                  return (
                    <AnimatedAccordion
                      key={integrationPair.codename}
                      checkBoxColor="default"
                      title={
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-left truncate">
                              {integrationPair.name}
                              {appIdsLength > 0 && (
                                <span className="ml-2 text-xs text-gray-500">
                                  {`( ${selectedAppIds.length} / ${appIdsLength} )`}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-left text-gray-400 truncate">
                              {integrationPair.user_name} • {integrationPair.total_accounts} accounts
                            </p>
                          </div>
                        </div>
                      }
                      checked={allChecked}
                      indeterminate={someChecked}
                      onToggleAll={(checked) =>
                        handleIntegrationPairToggle(pairId, checked, appIds)
                      }
                      expanded={!!expandedIntegrationPairs[integrationPair.codename]}
                      onToggleExpand={() => {
                        setExpandedIntegrationPairs((prev) => ({
                          ...prev,
                          [integrationPair.codename]: !prev[integrationPair.codename],
                        }));
                      }}
                    >
                      <div className="space-y-3">
                        {/* Display Google Ads and AdMob accounts */}
                        {/* <div className="px-8 space-y-2">
                          {integrationPair.google_ads_accounts?.length > 0 && (
                            <div className="bg-blue-50 rounded-lg p-2">
                              <p className="text-xs font-semibold text-blue-700 mb-1">Google Ads Accounts</p>
                              {integrationPair.google_ads_accounts.map((acc) => (
                                <div key={acc.id} className="text-xs text-gray-600 ml-2">
                                  • {acc.parent_name} ({acc.parent_id})
                                </div>
                              ))}
                            </div>
                          )}
                          {integrationPair.admob_accounts?.length > 0 && (
                            <div className="bg-green-50 rounded-lg p-2">
                              <p className="text-xs font-semibold text-green-700 mb-1">AdMob Accounts</p>
                              {integrationPair.admob_accounts.map((acc) => (
                                <div key={acc.id} className="text-xs text-gray-600 ml-2">
                                  • {acc.publisher_id}
                                </div>
                              ))}
                            </div>
                          )}
                        </div> */}

                        {/* Applications */}
                        {applications.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {applications.map((app, appIdx) => {
                              const id = appIds[appIdx];
                              return (
                                <Label
                                  key={app.codename}
                                  className="flex items-center gap-2 cursor-pointer px-8 py-2"
                                >
                                  <Checkbox
                                    color="secondary"
                                    checked={selectedPermissions.includes(id)}
                                    className="cursor-pointer select-none "
                                    onChange={(e) =>
                                      handleApplicationToggle(
                                        id,
                                        pairId,
                                        e.target.checked,
                                        appIds
                                      )
                                    }
                                  />
                                  <div className="min-w-8 min-h-8 w-8 h-8 sm:min-w-10 sm:min-h-10 sm:w-10 sm:h-10 flex justify-center items-center rounded-lg">
                                    <AppLogo
                                      logoUrl={app.logo}
                                      platform={app.platform}
                                      appName={app.name}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="truncate">{app.name}</p>
                                    <p className="text-xs text-gray-400">
                                      {app.platform}
                                    </p>
                                  </div>
                                </Label>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="px-8 py-4 text-center text-gray-500 text-sm">
                            No applications available for this integration pair
                          </div>
                        )}
                      </div>
                    </AnimatedAccordion>
                  );
                }
              )}
            </div>
          </Card>
        </div>
        <input
          type="hidden"
          {...register("selectedPermissions", {
            validate: (value) =>
              (value && value.length > 0) ||
              t("validation.atLeastOnePermission"),
          })}
        />
      </form>

      {/* Confirmation Modal */}
      <AnimatedModal
        show={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        title={
          isEditMode
            ? t("configurations.roles.confirmUpdateTitle")
            : t("configurations.roles.confirmCreateTitle")
        }
        icon={Shield}
        isConfirmation={true}
        onConfirm={handleConfirmCreate}
        confirmText={
          isEditMode
            ? t("configurations.roles.confirmUpdateConfirmButton")
            : t("configurations.roles.confirmCreateConfirmButton")
        }
        cancelText={t("common.cancel")}
        confirmButtonColor="indigo"
        size="md"
        isConfirming={isSubmitting}
      >
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-gray-700 mb-4">
              {isEditMode
                ? t("configurations.roles.confirmUpdateDescription", {
                  name: formData?.name ?? "",
                })
                : t("configurations.roles.confirmCreateDescription", {
                  name: formData?.name ?? "",
                })}
            </p>

            {!hasSelectedIntegrationPairs && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Warning size={24} className="text-amber-600" weight="fill" />
                  <div className="text-left">
                    <p className="text-amber-800 font-medium text-sm">
                      {t("configurations.roles.noIntegrationPairsSelectedTitle")}
                    </p>
                    <p className="text-amber-700 text-sm">
                      {t("configurations.roles.noIntegrationPairsSelectedDescription")}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </AnimatedModal>
    </>
  );
};

export default RoleForm;
